import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useDispatch } from 'src/store/useDispatch';

import PagePanel from 'src/components/PagePanel';
import { Table, type TableSort } from 'src/components/TableNew';
import PageHeader from 'src/components/PageHeader';
import NoResult from 'src/components/NoResult/NoResult';

import { BANK_ACCOUNTS_HEADERS } from 'src/utils/constants';
import { fetchBankAccounts } from 'src/store/slice/bankAccounts';

import BankAccountDetailModal from './BankAccountDetailModal';

// Columns the API can order by, mapped from the table's field name. The list
// endpoint has no search, so sorting is the only way to find a row — the header
// used to offer a search box whose parameters the API rejects.
const SORT_FIELDS: Record<string, string> = {
  account_number: 'account_number',
  bank_name: 'bank_name',
  status: 'status',
  created_at: 'created_at',
};

const BankAccounts = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sort, setSort] = useState<TableSort>(null);

  const dispatch = useDispatch();
  const {
    data: { bankAccounts, totalCount, totalPages },
    loading,
  } = useSelector(({ bankAccounts }) => bankAccounts);

  const items = bankAccounts ?? [];

  // A sort change re-runs the query from the first page.
  useEffect(() => {
    setCurrentPage(1);
  }, [sort]);

  useEffect(() => {
    dispatch(
      fetchBankAccounts({
        page: currentPage,
        sort: sort ? SORT_FIELDS[sort.field] : undefined,
        order: sort?.direction,
      }),
    );
  }, [dispatch, currentPage, sort]);

  return (
    <PagePanel>
      <PageHeader title={`Bank Accounts (${totalCount})`} hideSearch />
      {items.length > 0 || loading ? (
        <Table
          loading={loading}
          headers={BANK_ACCOUNTS_HEADERS}
          items={items}
          columns="1.2fr 1.2fr 1fr 0.9fr 1fr"
          fieldsToShow={['account_number', 'bank_name', 'swift_bic_code', 'status', 'created_at']}
          sortableFields={Object.keys(SORT_FIELDS)}
          sort={sort}
          onSortChange={setSort}
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          renderDetailsModal={(item, onClose) => (
            <BankAccountDetailModal account={item} onClose={onClose} />
          )}
        />
      ) : (
        <NoResult />
      )}
    </PagePanel>
  );
};

export default BankAccounts;
